import {useCallback, useEffect, useRef, useState} from "react"
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Radio, RadioGroup, TextField, Typography,
} from "@mui/material"
import {LocalShipping as ShippingIcon} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import {courierName, getEligibleCouriers} from "../../../data-tables-distributor/orders/courier-api"

const AssignCourierDialog = ({open, onClose, tenantId, order, canAssign, onConfirm, assignedCourier, reassign = false}) => {
    const {t, i18n} = useTranslation()
    const [couriers, setCouriers] = useState([])
    const [selected, setSelected] = useState("")
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState("")
    const [loadError, setLoadError] = useState("")
    const [reason, setReason] = useState("")
    const request = useRef(null)
    const submittingRef = useRef(false)
    const active = useRef(false)
    const language = i18n.language
    const excludedCourierId = reassign ? assignedCourier?.id : null

    const loadCouriers = useCallback(async () => {
        request.current?.abort()
        const controller = new AbortController()
        request.current = controller
        setLoading(true)
        setLoadError("")
        setSelected("")
        try {
            const result = await getEligibleCouriers(tenantId, language, controller.signal)
            if (!controller.signal.aborted) setCouriers(result.filter(profile => profile.id !== excludedCourierId))
        } catch (failure) {
            if (!controller.signal.aborted) {
                setCouriers([])
                setLoadError(failure.response?.data?.message || t("CouriersLoadFailed"))
            }
        } finally {
            if (!controller.signal.aborted) setLoading(false)
        }
    }, [tenantId, language, t, excludedCourierId])

    useEffect(() => {
        active.current = open
        if (open) {
            setError("")
            loadCouriers()
        }
        return () => { active.current = false; request.current?.abort() }
    }, [open, loadCouriers])

    const confirm = async () => {
        const courier = couriers.find(item => item.id === selected)
        if (!courier || !canAssign || submittingRef.current || (reassign && (!reason.trim() || reason.length > 1000))) return
        submittingRef.current = true
        setSubmitting(true)
        setError("")
        try {
            await onConfirm(courier, reason.trim())
        } catch (failure) {
            if (active.current) {
                setError(failure.response?.data?.message || t(reassign ? "CourierReassignmentFailed" : "CourierAssignmentFailed"))
                await loadCouriers()
            }
        } finally {
            submittingRef.current = false
            if (active.current) setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} disableEscapeKeyDown={submitting} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: "flex", alignItems: "center", gap: 1}}>
                <ShippingIcon color="primary"/>{t(reassign ? "ReassignCourier" : "AssignCourier")}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{mb: 2}}>{t("SelectCourierForOrder")} <strong>{order.orderNumber}</strong></Typography>
                {reassign && <>
                    <Typography sx={{mb: 2}}>{t("AssignedCourier")}: {courierName(assignedCourier)}</Typography>
                    <TextField label={t("CourierReassignmentReason")} required multiline minRows={2} fullWidth
                        value={reason} onChange={event => setReason(event.target.value)} disabled={submitting}
                        inputProps={{maxLength: 1000}} helperText={t("CourierReassignmentReasonHelp")} sx={{mb: 2}}/>
                </>}
                {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                {!canAssign && <Alert severity="info" sx={{mb: 2}}>
                    {assignedCourier ? `${t("AssignedCourier")}: ${courierName(assignedCourier)}` : t("CourierAssignmentNotAvailable")}
                </Alert>}
                {loadError && <Alert severity="error" action={<Button onClick={loadCouriers}>{t("Retry")}</Button>}>{loadError}</Alert>}
                {loading ? <Box sx={{display: "flex", justifyContent: "center", py: 3}}><CircularProgress aria-label={t("LoadingCouriers")}/></Box>
                    : !loadError && couriers.length === 0 ? <Alert severity="info">{t("NoEligibleCouriers")}</Alert>
                        : <RadioGroup aria-label={t("SelectCourier")} value={selected} onChange={event => setSelected(event.target.value)}>
                            {couriers.map(courier => <FormControlLabel key={courier.id} value={courier.id}
                                disabled={submitting || !canAssign} control={<Radio/>} sx={{m: 0, mb: 1, p: 1, border: 1, borderColor: "divider", borderRadius: 2}}
                                label={<Box>
                                    <Typography fontWeight={600}>{courierName(courier)}</Typography>
                                    <Typography variant="body2" color="text.secondary">{courier.userEmail}</Typography>
                                    <Chip size="small" color="success" label={t("CourierAvailable")} sx={{my: 1}}/>
                                    <Typography variant="body2">{t("CourierActiveOrders")}: {courier.activeOrderCount} / {courier.maxActiveOrders}</Typography>
                                    <Typography variant="body2">{t("CourierRemainingCapacity")}: {courier.remainingCapacity}</Typography>
                                </Box>}/>) }
                        </RadioGroup>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t("Cancel")}</Button>
                <Button onClick={confirm} variant="contained" disabled={!selected || loading || submitting || !canAssign || Boolean(loadError)
                    || (reassign && (!reason.trim() || reason.length > 1000))}
                    startIcon={submitting ? <CircularProgress size={16}/> : <ShippingIcon/>}>
                    {submitting ? t(reassign ? "ReassigningCourier" : "AssigningCourier") : t("Confirm")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AssignCourierDialog
